import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { useAuth } from '@/hooks/auth.hook';
import { useRootSelector } from '@/hooks/selector.hook';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';
import Logo from '@/assets/images/LOGO.png';
import LogoLight from '@/assets/images/logo-light.png';
import { IoIosLogOut } from 'react-icons/io';
import ThemeToggle from '@/components/Helper/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export default function KYCHeader() {
  const { resolvedTheme } = useTheme();
  const { logout, fetchProfile } = useAuth();
  const user = useRootSelector((state) => state.auth.user);
  const token = useRootSelector((state) => state.auth.token);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (token && !user) {
      fetchProfile();
    }
  }, [token, user, fetchProfile]);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  return (
    <header className="sticky top-0 z-30 w-full bg-white/80 dark:bg-[#0B0B0B]/80 backdrop-blur-md border-b border-gray-200/80 dark:border-[#222]">
      <div className="container flex items-center justify-between px-[1.6rem] sm:px-[2rem] py-[1.2rem] lg:py-[1.4rem]">
        {/* Logo bên trái */}
        <div className="flex items-center gap-[1rem]">
          <Link to="/dashboard" aria-label="Go to dashboard">
            <img
              src={resolvedTheme === 'dark' ? Logo : LogoLight}
              alt="nezenx"
              className="h-auto w-[9rem] lg:w-[12rem]"
            />
          </Link>
          <div className="lg:hidden">
            <ThemeToggle />
          </div>
        </div>

        {/* Desktop: User profile và logout button bên phải */}
        <div className="hidden lg:flex items-center gap-[1.4rem]">
          {/* User Profile */}
          <div className="flex items-center gap-[1rem] pr-[1.4rem] border-r border-gray-200 dark:border-[#262626]">
            <Avatar className="w-[3.6rem] h-[3.6rem]">
              <AvatarImage src={user?.avatar} alt={user?.email} />
              <AvatarFallback>
                {user?.firstName?.[0] || 'U'}
                {user?.lastName?.[0] || ''}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col leading-tight">
              <div className="text-[1.4rem] font-semibold dark:text-[#FFFFFF] text-[#000000]">
                {user?.firstName} {user?.lastName}
              </div>
              <div className="text-[1.15rem] dark:text-[#9A9A9A] text-[#7A7A7A]">
                {user?.roles?.length! > 0 ? user?.roles?.[0]?.name : 'User'}
              </div>
            </div>
          </div>

          <ThemeToggle />

          {/* Logout Button */}
          <Button
            variant="default"
            size="sz40"
            onClick={handleLogout}
          >
            <IoIosLogOut className="w-[1.7rem] h-[1.7rem] text-[#000000]" />
            <span className="text-[1.4rem] font-semibold text-[#000000]">Logout</span>
          </Button>
        </div>

        {/* Mobile: Dropdown với logo */}
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="second" className="lg:hidden flex items-center border-0 bg-transparent dark:bg-transparent">
              <Avatar className="w-[4rem] h-[4rem]">
                <AvatarImage src={user?.avatar} alt={user?.email} />
                <AvatarFallback>
                  {user?.firstName?.[0] || 'U'}
                  {user?.lastName?.[0] || ''}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[20rem] bg-[#161B19] border border-[#717171] rounded-[1.2rem] p-[1.5rem]"
          >
            {/* User Info */}
            <div className="flex flex-col gap-[1rem] mb-[1.5rem] pb-[1.5rem] border-b border-[#717171]">
              <div className="flex items-center gap-[1.2rem]">
                <Avatar className="w-[4rem] h-[4rem]">
                  <AvatarImage src={user?.avatar} alt={user?.email} />
                  <AvatarFallback>
                    {user?.firstName?.[0] || 'U'}
                    {user?.lastName?.[0] || ''}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <div className="text-[1.5rem] font-medium text-[#FFFFFF]">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-[1.2rem] text-[#BFBFBF]">
                    {user?.roles?.length! > 0 ? user?.roles?.[0]?.name : 'User'}
                  </div>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <Button
              variant="default"
              size="sz44"
              onClick={handleLogout}
            >
              <IoIosLogOut className="w-[1.8rem] h-[1.8rem] text-[#000000]" />
              <span className="text-[1.6rem] font-semibold text-[#000000]">Logout</span>
            </Button>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
